import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'

import { inlineTextRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type InlineTextProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    paragraph_1: string
    paragraph_2: string
    double_paragraph: boolean
    buttons: {
      link: WPLink
      variant: 'primary' | 'secondary'
    }[]
  }>

const InlineText = ({
  title,
  paragraph_1,
  paragraph_2,
  double_paragraph,
  buttons,
  ...props
}: InlineTextProps) => {
  return (
    <Motion animation={inlineTextRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid className="items-center gap-y-spacing-lg">
              {title ? (
                <div
                  className={cn(
                    'grid',
                    'col-span-2',
                    'md:col-span-3',
                    {
                      'lg:col-span-4':
                        double_paragraph || !buttons || buttons.length === 0
                    },
                    {
                      'lg:col-span-6':
                        !double_paragraph && (buttons?.length ?? 0) > 0
                    }
                  )}
                >
                  <Typography variant="h2" component="h2" data-motion-title>
                    {title}
                  </Typography>
                </div>
              ) : null}

              {paragraph_1 ? (
                <div
                  className={cn(
                    'col-span-2',
                    'md:col-span-3',
                    {
                      'lg:col-span-4':
                        double_paragraph || !buttons || buttons.length === 0
                    },
                    {
                      'lg:col-span-6':
                        !double_paragraph && (buttons?.length ?? 0) > 0
                    },
                    {
                      'lg:col-span-8':
                        !double_paragraph && (!buttons || buttons.length === 0)
                    }
                  )}
                >
                  <Typography variant="large" paragraph data-motion-paragraph1>
                    {paragraph_1}
                  </Typography>
                </div>
              ) : null}

              {paragraph_2 ? (
                <div
                  className={cn(
                    'col-span-2',
                    'md:col-span-3 md:col-start-4',
                    'lg:col-span-4 lg:col-start-9'
                  )}
                >
                  <Typography variant="large" paragraph data-motion-paragraph2>
                    {paragraph_2}
                  </Typography>
                </div>
              ) : null}

              {buttons?.length ? (
                <div className="col-span-2 flex gap-spacing-2xs md:col-span-6 lg:col-span-12">
                  {buttons.map((button: any, index: number) =>
                    button?.link?.url ? (
                      <Button
                        key={index}
                        variant={button?.variant}
                        asChild
                        data-motion-button
                      >
                        <Link
                          href={button.link.url}
                          target={button.link.target}
                          scroll={false}
                        >
                          <TextEffect>
                            <Typography variant="regular">
                              {button.link.title}
                            </Typography>
                          </TextEffect>
                        </Link>
                      </Button>
                    ) : null
                  )}
                </div>
              ) : null}
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default InlineText
